<template>
  <div id="quickquote-car-step-1-component">
    <h1-and-tooltip :h1="labelHeadingStep1Txt" :tooltip="tooltipStep1Txt" />

    <section>
      <article class="flow-group">
        <div v-if="!showCarsPrimaryUser" class="control">
          <!-- Beregn med nummerplade -->
          <div
            v-show="quickQuoteStepModelData.KnowPlateNumber"
            key="plateNumberDetails"
            class="control row push-up"
          >
            <div class="columns small-12 medium-8">
              <Field
                id="plateNumber"
                v-model="vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate"
                :rules="'required'"
                type="text"
                maxlength="7"
                :placeholder="contentModel.PlaceholderInputTheCarsLicensePlate"
                name="quickquote-car-input-platenumber"
                :aria-label="contentModel.PlaceholderInputTheCarsLicensePlate"
                data-vv-validate-on="blur"
                data-test="quickquote-car-input-platenumber"
                @input="revalidate($event)"
                @keyup.enter="fetchCarDetails"
              />
            </div>
            <div class="columns small-12 medium-4">
              <button
                class="button text-center"
                :disabled="!!searching"
                data-test="quickquote-car-button-searchplatenumber"
                @click="fetchCarDetails"
                v-html="contentModel.ButtonTextSearchForCarLicensePlate"
              />
              <div v-if="vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate">
                <ValidationError
                  name="quickquote-car-input-search"
                  :overrides="{ is: contentModel.ErrorSearchForCarLicensePlate }"
                />
                <Field
                  id="search"
                  v-model="validCar"
                  :rules="{ is: 'true' }"
                  type="hidden"
                  name="quickquote-car-input-search"
                  label="Bilen"
                  data-vv-validate-on="change"
                />
              </div>
            </div>
            <div class="small-12 columns">
              <ValidationError
                name="quickquote-car-input-platenumber"
                :overrides="{ required: contentModel.ErrorInputTheCarsLicensePlate }"
              />
            </div>
          </div>

          <!-- Beregn uden nummerplade -->
          <div
            v-show="!quickQuoteStepModelData.KnowPlateNumber && carInfo.carMakes !== null"
            key="manualCarDetails"
            class="push-up"
          >
            <div class="control row">
              <div class="small-12 columns">
                <LabelAndTooltip
                  for="CarMake"
                  :label="contentModel.LabelMake"
                  :tooltip="contentModel.TooltipMake"
                />
                <Field
                  v-slot="{ field, meta }"
                  v-model="vueModelData.TemplateModel.ManualCar.CarMake"
                  :rules="{ required: !quickQuoteStepModelData.KnowPlateNumber }"
                  name="quickquote-car-input-carmake"
                  label="Bilmærke"
                >
                  <VueSelect
                    id="CarMake"
                    :name="field.name"
                    :options="carInfo.carMakes"
                    :filter-by="forgivingMatch"
                    label="Description"
                    :placeholder="contentModel.LabelPickMake"
                    data-test="quickquote-car-select-carmake"
                    @search="carMakeSearch"
                    :model-value="vueModelData.TemplateModel.ManualCar.CarMake"
                    @search:blur="validateField(field.name)"
                    @update:model-value="
                      vueModelData.TemplateModel.ManualCar.CarMake = $event;
                      getCarModels();
                    "
                  >
                    <template v-if="showCarMakeFooter" #list-footer>
                      <li>
                        <a
                          v-if="carMakesFullListToggle"
                          class="plus-minus-link text-right"
                          data-test="quickquote-car-select-carmake-show-fewer"
                          @click="carMakeLoadAllMakes"
                        >
                          <span class="minus" /> {{ contentModel.LabelShowFewerMakes }}
                        </a>
                        <a
                          v-else
                          class="plus-minus-link text-right"
                          data-test="quickquote-car-select-carmake-show-more"
                          @click="carMakeLoadAllMakes"
                        >
                          <span class="plus" /> {{ contentModel.LabelShowMoreMakes }}
                        </a>
                      </li>
                    </template>
                  </VueSelect>
                  <ValidationError
                    name="quickquote-car-input-carmake"
                    :overrides="{ required: contentModel.ErrorPickMake }"
                  />
                </Field>
              </div>
            </div>
            <div
              v-show="
                vueModelData.TemplateModel.ManualCar.CarMake !== null && carInfo.carModels !== null
              "
              class="control row"
            >
              <div class="small-12 columns">
                <LabelAndTooltip
                  for="CarModel"
                  :label="contentModel.LabelModel"
                  :tooltip="contentModel.TooltipModel"
                />
                <Field
                  v-slot="{ field }"
                  v-model="vueModelData.TemplateModel.ManualCar.CarModel"
                  :rules="{ required: !quickQuoteStepModelData.KnowPlateNumber }"
                  name="quickquote-car-input-carmodel"
                  label="Bilmodel"
                >
                  <VueSelect
                    id="CarModel"
                    v-cloak
                    :name="field.name"
                    :options="carInfo.carModels"
                    :filter-by="forgivingMatch"
                    label="Description"
                    :placeholder="contentModel.LabelPickModel"
                    data-test="quickquote-car-select-carmodel"
                    @search:blur="validateField(field.name)"
                    :model-value="vueModelData.TemplateModel.ManualCar.CarModel"
                    @update:modelValue="
                      vueModelData.TemplateModel.ManualCar.CarModel = $event;
                      getCarYears();
                    "
                  />
                </Field>
                <ValidationError
                  name="quickquote-car-input-carmodel"
                  :overrides="{ required: contentModel.ErrorPickModel }"
                />
              </div>
            </div>

            <div
              v-show="
                vueModelData.TemplateModel.ManualCar.CarModel !== null && carInfo.carYears !== null
              "
              class="control row"
            >
              <div class="small-12 columns">
                <LabelAndTooltip
                  for="CarYear"
                  :label="contentModel.LabelYear"
                  :tooltip="contentModel.TooltipYear"
                />
                <Field
                  v-slot="{ field }"
                  :rules="{ required: !quickQuoteStepModelData.KnowPlateNumber }"
                  v-model="vueModelData.TemplateModel.ManualCar.CarYear"
                  label="Årgang"
                  name="quickquote-car-input-caryear"
                >
                  <VueSelect
                    id="CarYear"
                    :name="field.name"
                    v-cloak
                    :options="carInfo.carYears"
                    :filter-by="forgivingMatch"
                    label="Description"
                    :placeholder="contentModel.LabelPickYear"
                    data-test="quickquote-car-select-caryear"
                    @search:blur="validateField(field.name)"
                    :model-value="vueModelData.TemplateModel.ManualCar.CarYear"
                    @update:modelValue="
                      vueModelData.TemplateModel.ManualCar.CarYear = $event;
                      getCarVariants();
                    "
                  />
                </Field>
                <ValidationError name="quickquote-car-input-caryear" />
              </div>
            </div>

            <div
              v-show="
                vueModelData.TemplateModel.ManualCar.CarYear !== 0 &&
                vueModelData.TemplateModel.ManualCar.CarYear !== null &&
                carInfo.carVariants !== null
              "
              class="control row"
            >
              <div class="small-12 columns">
                <LabelAndTooltip
                  for="CarVariant"
                  :label="contentModel.LabelVariant"
                  :tooltip="contentModel.TooltipVariant"
                />
                <Field
                  v-slot="{ field }"
                  v-model="vueModelData.TemplateModel.ManualCar.CarVariant"
                  name="quickquote-car-input-carvariant"
                  label="Variant"
                  :rules="{ required: !quickQuoteStepModelData.KnowPlateNumber }"
                >
                  <VueSelect
                    id="CarVariant"
                    :name="field.name"
                    v-cloak
                    :options="carInfo.carVariants"
                    :filter-by="forgivingMatch"
                    label="Description"
                    :placeholder="contentModel.LabelPickVariant"
                    data-test="quickquote-car-select-carvariant"
                    @search:blur="validateField(field.name)"
                    :model-value="vueModelData.TemplateModel.ManualCar.CarVariant"
                    @update:modelValue="
                      vueModelData.TemplateModel.ManualCar.CarVariant = $event;
                      fetchCarDetails();
                    "
                  />
                </Field>
                <ValidationError name="quickquote-car-input-carvariant" />

                <p v-if="shouldShowVariantDescription" v-html="contentModel.VariantDescription" />
              </div>
            </div>
          </div>

          <!-- Hvis bilen er fundet -->
          <div
            v-if="quickQuoteStepModelData.carFound && vueModelData.TemplateModel.Asset"
            class="control row car-info-wrapper"
          >
            <div class="small-12 columns car-info-box">
              <p>
                {{ 'Din bil: ' }}
                <strong
                  v-for="(line, index) in vueModelData.TemplateModel.Asset.AssetDescriptionModel
                    .AssetDetailLines"
                  :key="'asset-detail-line-' + index"
                  data-test="quickquote-car-details"
                >
                  {{ line }}
                </strong>
              </p>
            </div>
          </div>
          <div v-if="quickQuoteStepModelData.details" class="control row car-info-wrapper">
            <div class="small-12 columns car-info-box">
              <p>
                {{ 'Din bil: ' }}
                <strong
                  data-test="quickquote-car-details"
                  v-html="quickQuoteStepModelData.details"
                />
              </p>
            </div>
          </div>

          <!-- Hvis bilen er leaset -->
          <div v-if="quickQuoteStepModelData.carFound" class="control row align-middle">
            <h3-and-tooltip
              class="small-12 columns"
              :h3="contentModel.LabelLeasedCar"
              :tooltip="contentModel.TooltipLeasedCar"
              id="labelLeasedCarQuestion"
            />
            <div class="small-12 medium-8 columns">
              <p data-test="quickquote-car-carfound-leased-question">
                {{ contentModel.LabelLeasedCarQuestion }}
              </p>
            </div>
            <div v-if="quickQuoteStepModelData.KnowPlateNumber" class="small-12 medium-4 columns" tabindex="0">
              <div role="radiogroup" aria-labelledby="labelLeasedCarQuestionSpan" tabindex="0">
                <div class="switch float-right" tabindex="0">
                  <Field
                    id="CarLeased-no"
                    v-model="vueModelData.TemplateModel.LicensePlateCar.CarLeased"
                    type="radio"
                    class="switch-input"
                    name="CarLeased"
                    :value="false"
                    data-test="quickquote-car-input-leased-car-no"
                    tabindex="0"
                  />
                  <label for="CarLeased-no" class="switch-label switch-label-left unselectable">{{
                    contentModel.SharedContent.LabelNo
                  }}</label>
                  <Field
                    id="CarLeased-yes"
                    v-model="vueModelData.TemplateModel.LicensePlateCar.CarLeased"
                    type="radio"
                    class="switch-input"
                    name="CarLeased"
                    :value="true"
                    data-test="quickquote-car-input-leased-car-yes"
                    tabindex="0"
                  />
                  <label for="CarLeased-yes" class="switch-label switch-label-right unselectable">{{
                    contentModel.SharedContent.LabelYes
                  }}</label>
                  <span class="selection-indicator" />
                </div>
              </div>
            </div>
            <div v-if="!quickQuoteStepModelData.KnowPlateNumber" class="small-12 medium-4 columns">
              <div role="radiogroup" aria-labelledby="labelLeasedCarQuestionSpan">
                <div class="switch float-right">
                  <Field
                    id="CarLeased-no"
                    v-model="vueModelData.TemplateModel.ManualCar.CarLeased"
                    type="radio"
                    class="switch-input"
                    name="CarLeased"
                    :value="false"
                    data-test="quickquote-car-input-leased-car-no"
                  />
                  <label for="CarLeased-no" class="switch-label switch-label-left unselectable">{{
                    contentModel.SharedContent.LabelNo
                  }}</label>
                  <Field
                    id="CarLeased-yes"
                    v-model="vueModelData.TemplateModel.ManualCar.CarLeased"
                    type="radio"
                    class="switch-input"
                    name="CarLeased"
                    :value="true"
                    data-test="quickquote-car-input-leased-car-yes"
                  />
                  <label for="CarLeased-yes" class="switch-label switch-label-right unselectable">{{
                    contentModel.SharedContent.LabelYes
                  }}</label>
                  <span class="selection-indicator" />
                </div>
              </div>
            </div>

            <div class="small-12 medium-4 columns">
              <h3-and-tooltip :h3="contentModel.LabelUsage" :tooltip="contentModel.TooltipUsage" />
              <p data-test="quickquote-car-carfound-usage">
                {{ contentModel.LabelUsagePrivate }}
              </p>
            </div>
          </div>

          <!-- Delt knap -->
          <div
            v-if="!quickQuoteStepModelData.carFound"
            class="columns small-12 medium-8 car-button-wrapper-left"
          >
            <button
              class="button tertiary"
              data-test="quickquote-car-button-know-plate-number-1"
              @click="
                toogleKnowPlateNumber();
                resetCarFound();
                resetCarDescription('All');
                checkClearAsset();
                clearValidation();
              "
              v-html="knowPlateNumberButtonTxt"
            />
          </div>
        </div>

        <!-- Om bilens primære bruger -->
        <div v-if="quickQuoteStepModelData.carFound && showCarsPrimaryUser">
          <div v-if="vueModelData.TemplateModel.Asset" class="control row car-info-wrapper">
            <div class="small-12 columns car-info-box">
              <p>
                {{ 'Din bil: ' }}
                <strong
                  v-for="(line, index) in vueModelData.TemplateModel.Asset.AssetDescriptionModel
                    .AssetDetailLines"
                  :key="'asset-detail-line-' + index"
                  data-test="quickquote-car-details"
                >
                  {{ line }}
                </strong>
              </p>
            </div>
          </div>
          <div v-if="quickQuoteStepModelData.details" class="control row car-info-wrapper">
            <div class="small-12 columns car-info-box">
              <p>
                {{ 'Din bil: ' }}
                <strong
                  data-test="quickquote-car-details"
                  v-html="quickQuoteStepModelData.details"
                />
              </p>
            </div>
          </div>

          <div class="control row">
            <div class="small-12 columns">
              <h3-and-tooltip
                id="labelYourAge"
                :h3="contentModel.LabelYourAge"
                :tooltip="contentModel.TooltipYourAge"
              />
                <Field
                  id="Age"
                  v-model="vueModelData.TemplateModel.MemberAge"
                  :rules="'required|numeric|min_value:18|max_value:110'"
                  type="text"
                  name="quickquote-car-input-age"
                  label="Alder"
                  data-test="quickquote-car-input-age"
                  :disabled="vueModelData.IsKnownMember"
                  aria-labelledby="labelYourAgeSpan"
                />
                <ValidationError
                  name="quickquote-car-input-age"
                  :overrides="{
                    min_value: contentModel.ErrorYourAge,
                    max_value: contentModel.ErrorYourAge
                  }"
                />
            </div>
          </div>
          <div class="control row">
            <div class="small-12 columns">
              <h3-and-tooltip id="postalCode" :h3="contentModel.LabelZip" :tooltip="contentModel.TooltipZip" />
                <Field
                  id="PostalCode"
                  v-model="vueModelData.TemplateModel.Address.adresse.postnr"
                  :rules="'required|numeric|min:4|max:4|validate_postalcode'"
                  type="text"
                  name="quickquote-car-input-postalcode"
                  max="4"
                  label="Postnummer"
                  data-vv-validate-on="blur"
                  data-test="quickquote-car-input-postalcode"
                  autocomplete="off"
                  :disabled="vueModelData.IsKnownMember"
                  @input="revalidate($event)"
                  aria-labelledby="postalCodeSpan"
                />
                <ValidationError
                  name="quickquote-car-input-postalcode"
                  :overrides="{ validate_postalcode: contentModel.ErrorZip }"
                />
            </div>
          </div>

          <div
            v-if="
              vueModelData.TemplateModel.CalculatedBonusLevel == null &&
              vueModelData.TemplateModel.Version == 1
            "
          >
            <div class="control row">
              <div class="small-12 columns">
                <h3-and-tooltip
                  :h3="contentModel.LabelYearsWithOwnInsurance"
                  :tooltip="contentModel.TooltipYearsWithOwnInsurance"
                  id="labelYearsWithOwnInsurance"
                />
                  <div role="radiogroup" aria-labelledby="labelYearsWithOwnInsuranceSpan">
                    <Field
                      v-slot="{ field }"
                      name="quickquote-car-input-yearswithcarinsurance"
                      v-model="vueModelData.TemplateModel.MemberYearsWithCarInsurance"
                      :rules="'required'"
                    >
                      <base-radio-button-pills
                        v-model="vueModelData.TemplateModel.MemberYearsWithCarInsurance"
                        :name="field.name"
                        :options="[
                          { label: '0', value: 0 },
                          { label: '1', value: 1 },
                          { label: '2', value: 2 },
                          { label: '3', value: 3 },
                          { label: '4', value: 4 },
                          { label: '5+', value: 5 }
                        ]"
                      />
                    </Field>
                  </div>
                <ValidationError
                  name="quickquote-car-input-yearswithcarinsurance"
                  :overrides="{ required: contentModel.ErrorYearsWithOwnInsurance }"
                />
              </div>
            </div>
          </div>

          <div
            v-if="
              quickQuoteStepModelData.carFound &&
              ((vueModelData.TemplateModel.Version == 1 &&
                vueModelData.TemplateModel.MemberYearsWithCarInsurance != null &&
                vueModelData.TemplateModel.MemberYearsWithCarInsurance > 0) ||
                vueModelData.TemplateModel.Version == 2)
            "
          >
                <div class="control row">
                  <div class="small-12 columns">
                    <h3-and-tooltip
                      :h3="contentModel.LabelHowManyFaultyClaimsHaveYouSubmitted"
                      :tooltip="contentModel.TooltipHowManyFaultyClaimsHaveYouSubmitted"
                      id="labelHowManyFaultyClaimsHaveYouSubmitted"
                    />
                      <div role="radiogroup" aria-labelledby="labelHowManyFaultyClaimsHaveYouSubmittedSpan">
                        <Field
                          v-slot="{ field }"
                          name="quickquote-car-input-numberofdamagingclaims"
                          v-model="vueModelData.TemplateModel.MemberNumberOfBurdeningClaims"
                          :rules="'required'"
                        >
                          <base-radio-button-pills
                            :name="field.name"
                            v-model="vueModelData.TemplateModel.MemberNumberOfBurdeningClaims"
                            :options="[
                              { label: '0', value: 0 },
                              { label: '1', value: 1 },
                              { label: '2', value: 2 },
                              { label: '3', value: 3 },
                              { label: '4+', value: 4 }
                            ]"
                          />
                        </Field>
                      </div>
                    <ValidationError
                      name="quickquote-car-input-numberofdamagingclaims"
                      :overrides="{ required: contentModel.ErrorHowManyFaultyClaimsHaveYouSubmitted }"
                    />
                  </div>
                </div>
                <div class="control row">
                  <div class="small-12 columns">
                    <h3-and-tooltip
                      :h3="contentModel.LabelHowManyFreeDamagesHaveYouReported"
                      :tooltip="contentModel.TooltipHowManyFreeDamagesHaveYouReported"
                      id="labelHowManyFreeDamagesHaveYouReported"
                    />
                      <div role="radiogroup" aria-labelledby="labelHowManyFreeDamagesHaveYouReportedSpan">
                        <Field
                          v-slot="{ field }"
                          name="quickquote-car-input-numberoffreeclaims"
                          v-model="vueModelData.TemplateModel.MemberNumberOfNonBurdeningClaims"
                          :rules="'required'"
                        >
                          <base-radio-button-pills
                            :name="field.name"
                            v-model="vueModelData.TemplateModel.MemberNumberOfNonBurdeningClaims"
                            :options="[
                              { label: '0', value: 0 },
                              { label: '1', value: 1 },
                              { label: '2', value: 2 },
                              { label: '3+', value: 3 }
                            ]"
                          />
                        </Field>
                      </div>
                      <ValidationError
                        name="quickquote-car-input-numberoffreeclaims"
                        :overrides="{ required: contentModel.ErrorHowManyFreeDamagesHaveYouReported }"
                      />
                  </div>
                </div>
          </div>
        </div>
      </article>

      <!-- Tilbage og Videre navigation -->
      <div v-if="quickQuoteStepModelData.carFound">
        <div class="control">
          <div class="small-12 columns">
            <div class="row push-down">
              <div
                v-if="!showCarsPrimaryUser"
                class="small-12 medium-6 columns car-button-wrapper-left"
              >
                <button
                  class="button tertiary"
                  data-test="quickquote-car-button-know-plate-number-2"
                  @click="
                    toogleKnowPlateNumber();
                    resetCarFound();
                    resetCarDescription('All');
                    checkClearAsset();
                    clearValidation();
                  "
                  v-html="knowPlateNumberButtonTxt"
                />
              </div>
              <div
                v-if="!showCarsPrimaryUser"
                class="small-12 medium-6 columns car-button-wrapper-right"
              >
                <button
                  class="button text-center"
                  :disabled="showCarsPrimaryUser"
                  data-test="quickquote-car-button-continue"
                  @click="toogleShowCarsPrimaryUser"
                  v-html="contentModel.LabelContinue"
                />
              </div>
              <div
                v-if="showCarsPrimaryUser"
                class="small-12 medium-6 columns car-button-wrapper-left"
              >
                <button
                  class="button tertiary"
                  data-test="quickquote-car-button-previous"
                  @click="toogleShowCarsPrimaryUser"
                  v-html="contentModel.SharedContent.LabelButtonPrevious"
                />
              </div>
              <div
                v-if="showCarsPrimaryUser"
                class="small-12 medium-6 columns car-button-wrapper-right"
              >
                <button
                  class="button text-center"
                  :disabled="disableNext"
                  data-test="quickquote-car-button-next"
                  @click="$emit('next')"
                  v-html="contentModel.SharedContent.LabelButtonNext"
                />
                <div
                  v-if="disableNext"
                  class="info-box is-open"
                  data-test="quickquote-car-next-info-box"
                >
                  <p>
                    <span v-html="contentModel.SharedContent.LabelModalContactContent" />
                    <strong v-html="contentModel.SharedContent.LabelModalContactTitle" /><br />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import QuickQuoteMixin from './QuickQuoteMixin.vue';
import { hasProperty } from '@/common/Util/utility.js';

import H3AndTooltip from '@/components/UI/H3AndTooltip.vue';
import H1AndTooltip from '@/components/UI/H1AndTooltip.vue';
import Utility from '@/common/UtilityFunctions.js';
import BaseRadioButtonPills from '@/components/Forms/BaseRadioButtonPills.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import { forEach } from 'lodash';
import axios from 'axios';
import VueSelect from '@/components/UI/VueSelect.vue';
import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';

export default {
  name: 'CarQuickQuote',
  components: {
    VueSelect,
    H1AndTooltip,
    H3AndTooltip,
    BaseRadioButtonPills,
    ValidationError
  },
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  emits: [
    'set-hide-next-button',
    'update-personal-information',
    'call-marketing-box-api',
    'next',
    'disable-next-button',
    'api-error'
  ],
  mixins: [QuickQuoteMixin],
  props: {
    contentModel: {
      type: Object,
      default() {
        return {};
      }
    },
    vueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    stepModel: {
      type: Object,
      default() {
        return {};
      }
    },
    quickQuoteStepModel: {
      type: Object,
      default() {
        return {
          carFound: false,
          KnowPlateNumber: true,
          details: null,
          carMake: null,
          carModel: null,
          carYear: null,
          carVariant: null
        };
      }
    },
    disableNext: {
      type: Boolean,
      default() {
        return false;
      }
    }
  },
  data() {
    return {
      searching: false,
      timer: null,
      c: 0,
      carInfo: {
        carMakes: [],
        carMakesCommon: [],
        carMakesFull: [],
        carModels: [],
        carYears: [],
        carVariants: []
      },
      showCarMakeFooter: true,
      showCarsPrimaryUser: false,
      carMakesFullListToggle: false,
      MemberNumberOfBurdeningClaimsWrongAnswer: 4,
      MemberNumberOfNonBurdeningClaimsWrongAnswer: 3,
      showCarVariantDescription: false,
      quickQuoteStepModelData: this.quickQuoteStepModel,
      vueModelData: this.vueModel
    };
  },
  computed: {
    validCar: {
      // getter
      get() {
        const out = '' + (this.quickQuoteStepModelData.carFound || this.searching);
        return out;
      },
      // setter
      set(newValue) {
        this.quickQuoteStepModelData.carFound = Boolean.parse(newValue);
      }
    },
    carType() {
      return this.quickQuoteStepModelData.KnowPlateNumber ? 'LicensePlateCar' : 'ManualCar';
    },
    personalInformationForTagManager() {
      return {
        event: 'track-event',
        eventCategory: this.getDanishProductTypeName + ' prisberegner',
        eventAction: '01 oplysninger submit ok',
        eventLabel: this.quickQuoteStepModelData.KnowPlateNumber
          ? 'med nummerplade'
          : 'uden nummerplade',
        eventValue: '0',
        eventNoninteraction: true,
        data: {
          nummerpladevaerdi:
            this.quickQuoteStepModelData.KnowPlateNumber && this.quickQuoteStepModelData.carFound
              ? this.vueModelData.TemplateModel.Asset.AssetDescriptionModel.AssetDetailSingleLine
              : '',
          nummerplade: this.quickQuoteStepModelData.KnowPlateNumber ? 'ja' : 'nej',
          bilmærke: this.quickQuoteStepModelData.carMake,
          bilmodel: this.quickQuoteStepModelData.carModel,
          årgang: this.quickQuoteStepModelData.carYear,
          variant: this.quickQuoteStepModelData.carVariant,
          leasingbil: this.vueModelData.TemplateModel[this.carType].CarLeased ? 'ja' : 'nej',
          anvendelse: 'Privatbil',
          alder: this.vueModelData.TemplateModel.MemberAge,
          postnummer: this.vueModelData.TemplateModel.Address.adresse.postnr,
          'bilforsikret-år':
            this.vueModelData.TemplateModel.MemberYearsWithCarInsurance === null ||
            typeof this.vueModelData.TemplateModel.MemberYearsWithCarInsurance === 'undefined'
              ? 'not applicable'
              : this.vueModelData.TemplateModel.MemberYearsWithCarInsurance
        }
      };
    },
    knowPlateNumberButtonTxt() {
      let txt = '';
      switch (this.quickQuoteStepModelData.KnowPlateNumber) {
        case true && typeof this.contentModel.LabelCalculateWithoutTheLicensePlate !== 'undefined':
          txt = this.contentModel.LabelCalculateWithoutTheLicensePlate;
          break;
        case false && typeof this.contentModel.LabelCalculateWithTheLicensePlate !== 'undefined':
          txt = this.contentModel.LabelCalculateWithTheLicensePlate;
          break;
        default:
          txt = '';
      }
      return txt;
    },
    labelHeadingStep1Txt() {
      let heading = '';
      if (
        this.quickQuoteStepModelData.KnowPlateNumber &&
        !this.showCarsPrimaryUser &&
        typeof this.contentModel.LabelHeadingStep1EnterPlateNumber !== 'undefined' &&
        this.contentModel.LabelHeadingStep1EnterPlateNumber !== ''
      ) {
        heading = this.contentModel.LabelHeadingStep1EnterPlateNumber;
      }
      if (
        !this.quickQuoteStepModelData.KnowPlateNumber &&
        !this.showCarsPrimaryUser &&
        typeof this.contentModel.LabelHeadingStep1FindCarModel !== 'undefined' &&
        this.contentModel.LabelHeadingStep1FindCarModel !== ''
      ) {
        heading = this.contentModel.LabelHeadingStep1FindCarModel;
      }
      if (
        this.quickQuoteStepModelData.carFound &&
        this.showCarsPrimaryUser &&
        typeof this.contentModel.LabelHeadingStep1AboutTheCarsPrimaryUser !== 'undefined' &&
        this.contentModel.LabelHeadingStep1AboutTheCarsPrimaryUser !== ''
      ) {
        heading = this.contentModel.LabelHeadingStep1AboutTheCarsPrimaryUser;
      }
      if (
        heading === '' &&
        typeof this.contentModel.SharedContent.LabelHeadingStep1 !== 'undefined' &&
        this.contentModel.SharedContent.LabelHeadingStep1 !== ''
      ) {
        heading = this.contentModel.SharedContent.LabelHeadingStep1;
      }
      return heading;
    },
    tooltipStep1Txt() {
      let tooltip = '';
      if (
        this.quickQuoteStepModelData.KnowPlateNumber &&
        !this.showCarsPrimaryUser &&
        typeof this.contentModel.TooltipKnowTheLicensePlate !== 'undefined'
      ) {
        tooltip = this.contentModel.TooltipKnowTheLicensePlate;
      }
      if (
        !this.quickQuoteStepModelData.KnowPlateNumber &&
        !this.showCarsPrimaryUser &&
        typeof this.contentModel.TooltipDontKnowTheLicensePlate !== 'undefined'
      ) {
        tooltip = this.contentModel.TooltipDontKnowTheLicensePlate;
      }
      if (
        this.quickQuoteStepModelData.carFound &&
        this.showCarsPrimaryUser &&
        typeof this.contentModel.TooltipAboutTheCarsPrimaryUser !== 'undefined'
      ) {
        tooltip = this.contentModel.TooltipAboutTheCarsPrimaryUser;
      }
      return tooltip;
    },
    shouldShowVariantDescription() {
      return this.showCarVariantDescription && this.contentModel.VariantDescription !== '';
    }
  },
  watch: {
    'quickQuoteStepModelData.KnowPlateNumber': function (val) {
      this.setVehicleDropdownsOnStep();
      if (val === false && val !== this.stepModel.KnowPlateNumber) {
        this.resetCarFound();
        this.setVehicleMakes();
        this.vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate = null;
      }
      this.emitPropValue('KnowPlateNumber', val);
    },
    'quickQuoteStepModelData.carFound': function (val) {
      if (!val) {
        this.clearValidation();
      }
      this.emitPropValue('carFound', val);
    },
    'stepModel.KnowPlateNumber': function (val) {
      if (val !== this.quickQuoteStepModelData.KnowPlateNumber) {
        this.quickQuoteStepModelData = val;
      }
    },
    'stepModel.carFound': function (val) {
      if (val !== this.quickQuoteStepModelData.carFound) {
        this.quickQuoteStepModelData.carFound = val;
      }
    },
    validCar() {
      if (this.vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate) {
        setTimeout(() => {
          this.form.validateField('quickquote-car-input-search');
        }, 500);
      }
    },
    'vueModelData.TemplateModel.MemberNumberOfBurdeningClaims': function (val) {
      this.warnAndBlocStep2(val, this.MemberNumberOfBurdeningClaimsWrongAnswer);
    },
    'vueModelData.TemplateModel.MemberNumberOfNonBurdeningClaims': function (val) {
      this.warnAndBlocStep2(val, this.MemberNumberOfNonBurdeningClaimsWrongAnswer);
    },
    personalInformationForTagManager() {
      this.$emit('update-personal-information', this.personalInformationForTagManager);
    },
    showCarsPrimaryUser: function (val) {
      if (val) {
        const earlyCallStepValue = 2;
        this.$emit('call-marketing-box-api', earlyCallStepValue);
      }
      if (!val) {
        const resetStepValue = 1;
        this.$emit('call-marketing-box-api', resetStepValue);
      }
    }
  },
  created() {
    if (this.vueModelData.TemplateModel.ManualCar.CarYear == 0)
      this.vueModelData.TemplateModel.ManualCar.CarYear = null;
  },
  mounted() {
    this.$emit('set-hide-next-button', true);

    this.setQuickQuoteStepModel();

    // Vue.set(this.vueModel, 'qste', this.quickQuoteStepModelData);
    this.vueModelData.qste = this.quickQuoteStepModelData;

    if (this.quickQuoteStepModelData.carFound) {
      this.showCarsPrimaryUser = true;
    }
  },
  methods: {
    test(event) {
      debugger;
    },
    decodeHtml: Utility.decodeHtml,
    fetchCarDetails() {
      // this.resetCarFound();
      if (this.quickQuoteStepModelData.KnowPlateNumber) {
        this.resetCarFound();
        if (
          this.vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate &&
          this.vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate !== ''
        ) {
          this.vueModelData.TemplateModel.LicensePlateCar.CarDmrLookup = true;
          this.vueModelData.TemplateModel.ShouldClearAsset = true;
          this.searching = true;
          this.updateQuickQuoteAsset(
            (response) => {
              this.checkLeasedCar(response);
              this.responseSuccess(response);

              // Utility.pushToTagManager({
              //     'event': 'track-event',
              //     'eventCategory': 'bil prisberegner',
              //     'eventAction': '01 oplysninger søg nummerplade succes',
              //     'eventLabel': '',
              //     'eventValue': '0',
              //     'eventNoninteraction': true
              // });

              this.$store.dispatch('Events/register', {
                event: 'quickquote-car-search-numberplate-succes',
                id: 'quickQuoteCar',
                vm: this
              });
            },
            () => {
              this.$emit('api-error', this.apiResponseError);
              this.resetCarFound();
              // this.$parent.$emit('showStdErrorModal');
              this.$parent.$parent.$parent.showQuickQuoteModalErrorValidation = false;
              this.$parent.$parent.$parent.showQuickQuoteModal = true;
              this.searching = false;

              // Utility.pushToTagManager({
              //     'event': 'track-event',
              //     'eventCategory': 'bil prisberegner',
              //     'eventAction': '01 oplysninger søg nummerplade error',
              //     'eventLabel': '',
              //     'eventValue': '0',
              //     'eventNoninteraction': true
              // });

              this.$store.dispatch('Events/register', {
                event: 'quickquote-car-search-numberplate-error',
                id: 'quickQuoteCar',
                vm: this
              });
            }
          );
        }
      } else {
        if (this.vueModelData.TemplateModel.ManualCar.CarVariant !== null) {
          if (this.vueModelData.TemplateModel.Asset) {
            this.resetCarFound();
          }

          this.quickQuoteStepModelData.carMake = `${this.vueModelData.TemplateModel.ManualCar.CarMake.Description}`;
          this.quickQuoteStepModelData.carModel = `${this.vueModelData.TemplateModel.ManualCar.CarModel.Description}`;
          this.quickQuoteStepModelData.carYear = `${this.vueModelData.TemplateModel.ManualCar.CarYear}`;
          this.quickQuoteStepModelData.carVariant = `${this.vueModelData.TemplateModel.ManualCar.CarVariant.Description}`;
          const details =
            this.quickQuoteStepModelData.carMake +
            ' ' +
            this.quickQuoteStepModelData.carModel +
            ' ' +
            this.quickQuoteStepModelData.carYear +
            ' ' +
            this.quickQuoteStepModelData.carVariant;

          // Vue.set(this.quickQuoteStepModelData, 'details', details);
          this.quickQuoteStepModelData.details = details;

          // Vue.set(this.quickQuoteStepModelData, 'carFound', true);
          this.quickQuoteStepModelData.carFound = true;

          this.postQuickQuoteAsset(
            this.vueModelData.ProductType,
            JSON.stringify(this.vueModelData)
          );
        }
      }
    },
    checkLeasedCar(data) {
      this.vueModelData.TemplateModel[this.carType].CarLeased =
        hasProperty(data, 'Asset') && hasProperty(data.Asset, 'Leased')
          ? data.Asset.Leased
          : this.vueModelData.TemplateModel[this.carType].CarLeased;
    },
    checkClearAsset() {
      const isChecked = this.quickQuoteStepModelData.KnowPlateNumber;
      this.vueModelData.TemplateModel.ShouldClearAsset = !isChecked;
    },
    resetCarFound() {
      this.quickQuoteStepModelData.carFound = false;
      this.quickQuoteStepModelData.details = null;
      delete this.vueModelData.TemplateModel.Coverage;
      delete this.vueModelData.TemplateModel.Asset;
    },
    resetCarDescription(model) {
      this.quickQuoteStepModelData.carFound = false;
      // Vue.set(this.quickQuoteStepModelData, 'details', null);
      this.quickQuoteStepModelData.details = null;
      switch (model) {
        case 'All':
          this.vueModelData.TemplateModel.ManualCar.CarMake = null;
          this.quickQuoteStepModelData.carMake = null;
          this.vueModelData.TemplateModel.ManualCar.CarModel = null;
          this.quickQuoteStepModelData.carModel = null;
          this.vueModelData.TemplateModel.ManualCar.CarYear = null;
          this.quickQuoteStepModelData.carYear = null;
          this.vueModelData.TemplateModel.ManualCar.CarVariant = null;
          this.quickQuoteStepModelData.carVariant = null;
          this.vueModelData.TemplateModel.ManualCar.CarLeased = false;
          this.vueModelData.TemplateModel.LicensePlateCar.CarLeased = false;
          this.vueModelData.TemplateModel.LicensePlateCar.CarDmrLookup = false;
          this.vueModelData.TemplateModel.LicensePlateCar.CarLicensePlate = '';
          break;
        case 'CarMake':
          this.vueModelData.TemplateModel.ManualCar.CarModel = null;
          this.vueModelData.TemplateModel.ManualCar.CarYear = null;
          this.vueModelData.TemplateModel.ManualCar.CarVariant = null;
          break;
        case 'CarModel':
          this.vueModelData.TemplateModel.ManualCar.CarYear = null;
          this.vueModelData.TemplateModel.ManualCar.CarVariant = null;
          break;
        case 'CarYear':
          this.vueModelData.TemplateModel.ManualCar.CarVariant = null;
          break;
      }
    },
    setVehicleMakes() {
      if (this.carInfo.carMakes.length === 0) {
        this.getDropdownValues('getmakes', 'carMakes');
        this.vueModelData.TemplateModel.ShouldClearAsset = true;
      }
    },
    updateVehicleModels() {
      if (this.vueModelData.TemplateModel.ManualCar.CarMake !== null) {
        this.getDropdownValues(
          'GetModels?makeId=' + this.vueModelData.TemplateModel.ManualCar.CarMake.Id,
          'carModels'
        );
        this.vueModelData.TemplateModel.ShouldClearAsset = true;
      }
    },
    updateVehicleYears() {
      if (this.vueModelData.TemplateModel.ManualCar.CarModel !== null) {
        this.getDropdownValues(
          'GetYears?modelId=' + this.vueModelData.TemplateModel.ManualCar.CarModel.Id,
          'carYears'
        );
        this.vueModelData.TemplateModel.ShouldClearAsset = true;
      }
    },
    updateVehicleVariants() {
      if (
        this.vueModelData.TemplateModel.ManualCar.CarModel !== null &&
        this.vueModelData.TemplateModel.ManualCar.CarYear !== null &&
        this.vueModelData.TemplateModel.ManualCar.CarYear !== 0
      ) {
        this.getDropdownValues(
          'GetVariants?modelId=' +
            this.vueModelData.TemplateModel.ManualCar.CarModel.Id +
            '&year=' +
            this.vueModelData.TemplateModel.ManualCar.CarYear,
          'carVariants'
        );
        this.vueModelData.TemplateModel.ShouldClearAsset = true;
      }
    },
    getDropdownValues(selector, dropdownCase) {
      if (dropdownCase !== '') {
        return axios
          .get(Utility.constants().API.proposal_flow_vehicle + selector)
          .then((response) => {
            this.setDropdownValues(dropdownCase, response.data);
            this.clearValidation();

            if (this.carInfo.carVariants.length > 1) {
              this.showCarVariantDescription = true;
            } else if (this.showCarVariantDescription) this.showCarVariantDescription = false;
          })
          .catch((err) => {
            this.showErrorModal(err);
          });
      }
    },
    setDropdownValues(dropdown, values) {
      if (hasProperty(values, 'CommonMakes')) {
        this.carInfo[dropdown] = values.CommonMakes;
        this.carInfo.carMakesFull = values.AllMakes;
        this.carInfo.carMakesCommon = values.CommonMakes;
      } else {
        this.carInfo[dropdown] = values;
      }
    },
    responseSuccess(response) {
      this.vueModelData.TemplateModel.LicensePlateCar.CarDmrLookup = false;
      this.vueModelData.TemplateModel.ShouldClearAsset = false;
      this.quickQuoteStepModelData.carFound = true;
      this.searching = false;
      this.handleResponse(
        response,
        () => {
          // this.$parent.$emit('showQuickQuoteModalErrorValidation', true);
          this.$parent.showQuickQuoteModalErrorValidation = true;
          this.$parent.showQuickQuoteModal = true;
        },
        false
      );
    },
    emitPropValue(name, value) {
      // this.$parent.$emit('stepModelProp', name, value);
      this.$parent.$parent.$parent.setStepModelProp(name, value);
    },
    setQuickQuoteStepModel() {
      const self = this;
      forEach(this.stepModel, (val, key) => {
        self.quickQuoteStepModelData[key] = val;
      });
    },
    carMakeSearch(search) {
      this.carInfo.carMakes =
        search !== '' && this.carInfo.carMakesFull.length > 0
          ? this.carInfo.carMakesFull
          : this.carInfo.carMakesCommon;
      this.showCarMakeFooter = search !== '' ? false : true;
    },
    carMakeLoadAllMakes() {
      this.carMakesFullListToggle = !this.carMakesFullListToggle;
      this.carInfo.carMakes = this.carMakesFullListToggle
        ? this.carInfo.carMakesFull
        : this.carInfo.carMakesCommon;
    },
    getCarModels(te) {
      this.resetCarDescription('CarMake');

      this.updateVehicleModels();
    },
    getCarYears() {
      this.resetCarDescription('CarModel');
      this.updateVehicleYears();
    },
    getCarVariants() {
      this.resetCarDescription('CarYear');
      this.updateVehicleVariants();
    },
    warnAndBlocStep2(val, wrongAnswer) {
      if (val >= wrongAnswer) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      }
      if (
        this.vueModelData.TemplateModel.MemberNumberOfBurdeningClaims <
          this.MemberNumberOfBurdeningClaimsWrongAnswer &&
        this.vueModelData.TemplateModel.MemberNumberOfNonBurdeningClaims <
          this.MemberNumberOfNonBurdeningClaimsWrongAnswer
      ) {
        this.$emit('disable-next-button', false);
      }
    },
    setVehicleDropdownsOnStep() {
      this.updateVehicleModels();
      this.updateVehicleYears();
      this.updateVehicleVariants();
    },
    updateClaims() {
      if (this.vueModelData.TemplateModel.MemberYearsWithCarInsurance === 0) {
        this.vueModelData.TemplateModel.MemberNumberOfBurdeningClaims = null;
        this.vueModelData.TemplateModel.MemberNumberOfNonBurdeningClaims = null;
      }
    },
    forgivingMatch(option, label, search) {
      var normalizedLabel = label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      var normalizedSearch = search
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      var result = normalizedLabel.indexOf(normalizedSearch);
      return result > -1;
    },
    toogleKnowPlateNumber() {
      this.quickQuoteStepModelData.KnowPlateNumber = !this.quickQuoteStepModelData.KnowPlateNumber;
    },
    toogleShowCarsPrimaryUser() {
      this.showCarsPrimaryUser = !this.showCarsPrimaryUser;
    },
    clearValidation() {
      this.form.setErrors({});
    }
  }
};
</script>

<style lang="scss" scoped>
.car-info-wrapper {
  margin: 0px;
  padding-bottom: 2rem;
}

.car-info-box {
  background-color: #f7f7f7;
  padding: 20px;

  p {
    margin-top: 0px;
    margin-bottom: 0px;
  }
}

.car-button-wrapper-left {
  padding-left: 0px;
}

.car-button-wrapper-right {
  padding-right: 0px;
}
</style>
